// ** React Imports
import React, { useEffect, useState } from 'react'

// ** MUI Imports
import { Button, Grid, Typography } from '@mui/material'
import { GridColumns, GridRenderCellParams } from '@mui/x-data-grid-pro'

// ** Child Imports
import { ArrowDownRight, ArrowUpRight, Download, Minus, TrendingDown, TrendingUp } from 'react-feather'

// ** Util Imports
import { WidgetType } from 'src/lib/types/dashboardAnalytics'
import { arrayToCsv, downloadCsvHelper, numberWithCommas } from 'src/lib/utils/helpers'
import { useFilter } from 'src/lib/contexts/FilterContext'
import { extractDateFromFilters, getAnalyticsWidgetPayload } from 'src/lib/utils/analyticsHelpers'
import { analyticsConstants } from 'src/lib/utils/constants'
import { useLocale } from 'src/lib/hooks/useLocal'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import GenericTable from 'src/components/common/table/GenericTable'
import { EntityName, useActivityLogs } from 'src/lib/hooks/useActivityLogs'

const DiscountFailureRatesTable: React.FC<WidgetType> = ({
  value,
  hideDownloadReport = true,
  isLoading,
  error,
  triggerRefresh,
  triggerDownloadReport,
  downloadTriggered,
  hideBorder = false,
  hideRowDivider = false,
  isPaginationEnabled = true,
  hideHeader = false,
  hideFooter = false,
  removeCellPadding = false,
  downloadReportPosition = { top: -98, right: 0 },
  isReportView = false,
  moreIsGood,
  hasComparison
}) => {
  const { analyticsFilters } = useFilter()
  const { t } = useLocale()
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10
  })
  const { sendActivityLog } = useActivityLogs()
  const newrowsPerPage = pagination.pageSize
  const newpage = pagination.pageIndex

  const payload = {
    entityName: EntityName.OVERVIEW,
    actionName: 'downloaded report'
  }

  useEffect(() => {
    if (typeof value !== 'undefined') setPagination({ ...pagination, pageIndex: 0 })
  }, [value])

  useEffect(() => {
    if (downloadTriggered) {
      downloadCsvHelper(
        arrayToCsv(value, ['Discount code', 'Discount selected', 'Discount code entered', 'Failed application']),
        'discount_failure_report.csv'
      )
      sendActivityLog(payload)
      typeof triggerDownloadReport !== 'undefined' && triggerDownloadReport(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadTriggered])

  const handlePageChange = async (newPage: number) => {
    setPagination({ ...pagination, pageIndex: newpage })
  }

  const handleChangeRowsPerPage = (pageSize: number) => {
    triggerRefresh(
      getAnalyticsWidgetPayload(
        analyticsConstants.FLO_DISCOUNT_FAILURE_RATES,
        extractDateFromFilters(analyticsFilters, 'startDate'),
        extractDateFromFilters(analyticsFilters, 'endDate')
      )
    )
    setPagination({ ...pagination, pageSize: newrowsPerPage })
    setPagination({ ...pagination, pageIndex: 0 })
  }

  const widgetColumns: ColumnDef<any>[] = [
    {
      header: 'Discount code',
      cell: ({ row }: { row: any }) => {
        const value = row.original['code']
        const sececondayValue = row.original['discounts_selected'] + row.original['codes_entered']

        return (
          <div className='flex flex-col w-52'>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 400,
                color: 'text.header',
                lineHeight: '18px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {value}
            </Typography>
            <Typography variant='body2' sx={{ color: '#949494', lineHeight: '18px' }}>
              {numberWithCommas(sececondayValue, 0)} attempts
            </Typography>
          </div>
        )
      }
    },
    {
      header: 'Failure rate',
      cell: ({ row }: { row: any }) => {
        const value = row.original['failure_rate']
        return (
          <p
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'start',
              margin: 0,
              fontWeight: 500,
              color: '#4d4d4d'
            }}
          >
            <span>{value}</span>
          </p>
        )
      }
    }
    // {
    //   field: 'compareToVal',
    //   maxWidth: hasComparison ? 70 : 0,
    //   width: hasComparison ? 70 : 0,
    //   minWidth: hasComparison ? 70 : 0,
    //   sortable: false,
    //   renderCell: (params: GridRenderCellParams<any>) => {
    //     const row = params?.row
    //     const value = row['compareToVal']
    //     const isGood = moreIsGood ? Boolean(parseInt(value) > 0) : Boolean(parseInt(value) < 0)

    //     return hasComparison ? (
    //       <p
    //         style={{
    //           width: '100%',
    //           display: 'flex',
    //           justifyContent: 'end',
    //           alignItems: 'center',
    //           margin: 0,
    //           gap: 2,
    //           fontWeight: 600,
    //           color: isGood ? '#2C874A' : '#CB2711'
    //         }}
    //       >
    //         {Boolean(parseInt(value)) && !value.includes('Infinity') ? (
    //           isGood ? (
    //             <ArrowDownRight className='h-4 w-4 stroke-[2.5px]' />
    //           ) : (
    //             <ArrowUpRight className='h-4 w-4 stroke-[2.5px]' />
    //           )
    //         ) : (
    //           <></>
    //         )}
    //         {Boolean(parseInt(value)) && !value.includes('Infinity') ? (
    //           <>
    //             <> {Math.sign(parseInt(value)) === -1 ? value?.slice(1) : value}</>
    //           </>
    //         ) : (
    //           <Minus className='text-[#949494] w-[14px] stroke-[2.5px]' />
    //         )}
    //       </p>
    //     ) : (
    //       <></>
    //     )
    //   }
    // }
  ]

  const reportColumns: ColumnDef<any>[] = [
    {
      header: 'Discount code',
      cell: ({ row }: { row: any }) => {
        const value = row.original['code']
        return (
          <div className='flex flex-col w-52'>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 400,
                color: 'text.header',
                lineHeight: '18px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {value}
            </Typography>
          </div>
        )
      }
    },
    {
      header: 'Discount selected',
      cell: ({ row }: { row: any }) => {
        const value = row.original['discounts_selected']
        return (
          <Typography
            variant='body2'
            sx={{
              fontWeight: 400,
              color: 'text.header',
              lineHeight: '18px',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {value}
          </Typography>
        )
      }
    },
    {
      header: 'Discount code entered',
      cell: ({ row }: { row: any }) => {
        const value = row.original['codes_entered']
        return (
          <Typography
            variant='body2'
            sx={{
              fontWeight: 400,
              color: 'text.header',
              lineHeight: '18px',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {value}
          </Typography>
        )
      }
    },
    {
      header: 'Failed applications',
      cell: ({ row }: { row: any }) => {
        const value = row.original['failure_rate']
        const secondaryValue = row.original['failed_attempts']
        return (
          <div className='flex justify-between w-full'>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 400,
                color: 'text.header',
                lineHeight: '18px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {secondaryValue}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 400,
                color: 'text.header',
                lineHeight: '18px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {value}
            </Typography>
          </div>
        )
      }
    }
  ]

  return (
    <div className='flex flex-col w-full'>
      <Grid item sx={{ width: '100%', position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            width: '50%',
            justifyContent: 'end',
            position: 'absolute',
            top: downloadReportPosition?.top,
            right: downloadReportPosition?.right
          }}
        >
          {!Boolean(hideDownloadReport) ? (
            <Button
              variant='outlined'
              sx={{ height: 36, position: 'absolute', top: -130 }}
              disabled={!value?.length}
              onClick={() => {
                downloadCsvHelper(
                  arrayToCsv(value, [
                    'Discount code',
                    'Discount selected',
                    'Discount code entered',
                    'Failed application',
                    'Failure rate'
                  ]),
                  'discount_failure_report.csv'
                )
                sendActivityLog(payload)
              }}
            >
              <Download style={{ transform: 'scale(0.75)' }} />
              {t('download_report')}
            </Button>
          ) : (
            <></>
          )}
        </div>
      </Grid>
      <GenericTable
        data={
          isReportView
            ? [...value?.slice(newpage * newrowsPerPage, newpage * newrowsPerPage + newrowsPerPage)]
            : [...value?.slice(newpage * 5, newpage * 5 + 5)]
        }
        columns={isReportView ? reportColumns : widgetColumns}
        pagination={{
          paginationState: pagination,
          setPaginationState: setPagination
        }}
        totalRows={value?.length ?? 0}
        loading={isLoading}
        customPaginationConfig={{
          customPagination: false
        }}
        isCustomizable={{
          isFullWidth: false,
          isResizable: false,
          hideDropDown: !isReportView,
          hidePaginationButton: !isReportView
        }}
      />
    </div>
  )
}

export default DiscountFailureRatesTable
